import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { usePreviewAdHocCommunication } from '../../../lib/services/communication.service';
import { flushComposeAudience } from '../../../lib/stores/communication.store';
import {
  deliveryOptionRequiresEmail,
  deliveryOptionRequiresSMS
} from '../../../lib/helpers/communication.helpers';
import {
  CommunicationContextType,
  IAdHocCommunicationPreview,
  IAdHocContentInput,
  IAdHocTokenReport,
  IComposeAudienceSelection
} from '../../../lib/types';

interface UseFreshTokenReportArgs {
  debouncedSelection: IComposeAudienceSelection;
  isPreviewStale: boolean;
  panelAssessmentId?: string;
  debouncedContext: { contextType?: CommunicationContextType; contextId?: string };
  isPanelOpen: boolean;
  // The "preview as" recipient the panel renders; omitted → server-default first.
  playerAssessmentId?: string;
  // Requested recipient fell out but others remain — show the notice.
  onRecipientFellOut?: () => void;
  // Audience emptied under the requested recipient — clear it, no notice.
  onRecipientCleared?: () => void;
}

interface UseFreshTokenReportReturn {
  latestReport?: IAdHocTokenReport;
  waitForFreshReport: () => Promise<IAdHocTokenReport | undefined>;
  // Full server-rendered preview for the current recipient; feeds the preview panel.
  preview?: IAdHocCommunicationPreview;
  previewLoading: boolean;
}

// HOOK: owns the compose panel's server token-report freshness gate — the debounced
// preview query, the held latest report, and the submit-time wait for a fresh result.
export const useFreshTokenReport = ({
  debouncedSelection,
  isPreviewStale,
  panelAssessmentId,
  debouncedContext,
  isPanelOpen,
  playerAssessmentId,
  onRecipientFellOut,
  onRecipientCleared
}: UseFreshTokenReportArgs): UseFreshTokenReportReturn => {
  // The ONE debounced preview/validation call (the preview panel reuses it) — content
  // mirrors the send's channel gating so an unused surface never blocks submit.
  const previewContent = useMemo<IAdHocContentInput>(() => {
    const usesEmail = deliveryOptionRequiresEmail(debouncedSelection.deliveryOption);
    const usesSMS = deliveryOptionRequiresSMS(debouncedSelection.deliveryOption);
    return {
      subject: usesEmail ? debouncedSelection.content.subject : undefined,
      bodyHtml: usesEmail ? debouncedSelection.content.bodyHtml : undefined,
      smsBody: usesSMS ? debouncedSelection.content.smsBody : undefined
    };
  }, [debouncedSelection]);
  const hasPreviewContent = !!(previewContent.subject || previewContent.bodyHtml || previewContent.smsBody);
  // A parked submit keeps the query alive through a panel close so the send
  // completes in the background seconds later — never wedged, never deferred.
  const [isAwaitingReport, setIsAwaitingReport] = useState(false);
  const isPreviewActive = isPanelOpen || isAwaitingReport;
  const { preview, loading: previewLoading, error: previewError } = usePreviewAdHocCommunication({
    assessmentId: isPreviewActive ? panelAssessmentId : undefined,
    criteria: debouncedSelection.audience,
    contextType: debouncedContext.contextType,
    contextId: debouncedContext.contextId,
    content: previewContent,
    playerAssessmentId,
    skip: !hasPreviewContent
  });

  // Reconcile the requested recipient against what the server settled on: a
  // different one fell out (notify); none means the audience emptied (clear).
  useEffect(() => {
    if (previewLoading || !playerAssessmentId || !preview) return;
    if (preview.playerAssessmentId === playerAssessmentId) return;
    if (preview.playerAssessmentId) onRecipientFellOut?.();
    else onRecipientCleared?.();
  }, [previewLoading, playerAssessmentId, preview, onRecipientFellOut, onRecipientCleared]);

  // Latest settled token report, held so the issue indicator doesn't blank
  // while a no-cache refresh is in flight.
  const [latestReport, setLatestReport] = useState<IAdHocTokenReport | undefined>();
  useEffect(() => {
    if (preview) setLatestReport(preview.report);
    else if (!hasPreviewContent) setLatestReport(undefined);
  }, [preview, hasPreviewContent]);

  // Freshness = the query is live AND the report matches the form as typed. A
  // preview outage counts as fresh-with-no-report (server backstop nets sends).
  const isReportFresh = isPreviewActive
    && !isPreviewStale
    && !previewLoading
    && (!hasPreviewContent || !!preview || !!previewError);
  // The gate reads the live query result only — never latestReport (indicator
  // display state); a preview outage leaves it undefined and the server backstops.
  const settledReport = preview ? preview.report : undefined;
  const reportSyncRef = useRef<{ fresh: boolean; report?: IAdHocTokenReport }>({
    fresh: true,
    report: undefined
  });
  const reportWaitersRef = useRef<Array<(report?: IAdHocTokenReport) => void>>([]);
  useEffect(() => {
    reportSyncRef.current = { fresh: isReportFresh, report: settledReport };
    if (!isReportFresh || !reportWaitersRef.current.length) return;
    const waiters = reportWaitersRef.current;
    reportWaitersRef.current = [];
    setIsAwaitingReport(false);
    waiters.forEach((resolve) => resolve(settledReport));
  }, [isReportFresh, settledReport]);

  // Submit gate: lands any pending debounce push now, then resolves with a
  // report computed for the CURRENT content — never a stale one.
  const waitForFreshReport = useCallback((): Promise<IAdHocTokenReport | undefined> => {
    flushComposeAudience();
    if (reportSyncRef.current.fresh) return Promise.resolve(reportSyncRef.current.report);
    setIsAwaitingReport(true);
    return new Promise((resolve) => {
      let timer: number;
      const settle = (report?: IAdHocTokenReport) => {
        window.clearTimeout(timer);
        reportWaitersRef.current = reportWaitersRef.current.filter((w) => w !== settle);
        resolve(report);
      };
      // A hung preview must never wedge the submit — after 5s resolve with no
      // report and let the server backstop validate.
      timer = window.setTimeout(() => { setIsAwaitingReport(false); settle(undefined); }, 5000);
      reportWaitersRef.current.push(settle);
    });
  }, []);

  return { latestReport, waitForFreshReport, preview, previewLoading };
};
